/**
 * Payload schemaVersion 1, chartType comboBarLineDualAxis:
 * - categories: [{ key, label }]
 * - series: [{ key, label, type: 'bar'|'line', axis: 'left'|'right', format: 'percentage'|'currency'|'days', color, order }]
 * - values: { [seriesKey]: { [categoryKey]: number } } ou, com weekKeys, { [seriesKey]: { [monthKey]: { [weekKey]: number } } }.
 *   Com valores semanais em dias: cada ponto mensal = média das 4 semanas.
 *
 * Modo % + R$ (compras): barras % no eixo esquerdo (0–100); linha no direito (currency ou days).
 * Modo só dias: todas as séries com format 'days' → um eixo Y (esquerda); valores podem ter até 2 decimais.
 *
 * - options.leftAxis: em modo % ignorado na escala (0–100, step 20). Em modo days: { format:'days', min?, max?, step? }.
 * - options.rightAxis: só no modo duplo; max sobe com os dados se necessário.
 * - options.xAxis.monthTickPattern: 'ptShortAlternate' → eixo X horizontal, Jan/Mar/Mai… (um sim, um não).
 */
const MONTH_KEY_TO_PT_SHORT = {
  JAN: 'Jan',
  FEV: 'Fev',
  MAR: 'Mar',
  ABR: 'Abr',
  MAI: 'Mai',
  JUN: 'Jun',
  JUL: 'Jul',
  AGO: 'Ago',
  SET: 'Set',
  OUT: 'Out',
  NOV: 'Nov',
  DEZ: 'Dez',
}

const ZWSP = '\u200b'

function buildAlternateShortMonthLabels(categories) {
  return categories.map((c, i) => {
    if (i % 2 !== 0) return ZWSP
    const mk = String(c?.key ?? '')
    return MONTH_KEY_TO_PT_SHORT[mk] || String(c?.label ?? '').slice(0, 3)
  })
}

function formatTooltipValue(value, format) {
  const n = Number(value)
  if (format === 'currency') {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number.isFinite(n) ? n : 0)
  }
  if (format === 'percentage') {
    return `${Number.isFinite(n) ? n : 0}%`
  }
  if (format === 'days') {
    const x = Number.isFinite(n) ? n : 0
    const formatted = new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(x)
    const unit = x === 1 && Number.isInteger(x) ? 'dia' : 'dias'
    return `${formatted} ${unit}`
  }
  return String(value)
}

function maxValueForYAxisId(datasets, axisId) {
  let m = 0
  for (const ds of datasets || []) {
    if (ds.yAxisID !== axisId) continue
    const arr = Array.isArray(ds.data) ? ds.data : []
    for (const v of arr) {
      const n = Number(v)
      if (Number.isFinite(n)) m = Math.max(m, n)
    }
  }
  return m
}

/** Evita barras/linha “no teto”: se os dados passam do max configurado, sobe o eixo com folga e alinha ao step. */
function tickMaxFromConfigAndData(configMax, dataMax, step) {
  const hasConfig = Number.isFinite(configMax)
  const s = Number.isFinite(step) && step > 0 ? step : null
  const ceilStep = (v) => (s ? Math.ceil(v / s) * s : Math.ceil(v * 100) / 100)

  if (!hasConfig) {
    if (!(dataMax > 0)) return undefined
    return ceilStep(dataMax * 1.12)
  }

  if (dataMax < configMax) return configMax

  const headroom = Math.max(dataMax, configMax) * 1.12
  return Math.max(ceilStep(headroom), configMax)
}

function usesNestedWeekValues(p) {
  const wk = p.weekKeys
  if (!Array.isArray(wk) || wk.length === 0) return false
  const cats = Array.isArray(p.categories) ? p.categories : []
  const series = Array.isArray(p.series) ? p.series : []
  if (!cats.length || !series.length) return false
  const mk = String(cats[0]?.key ?? '')
  if (!mk) return false
  for (const s of series) {
    const sk = String(s?.key ?? '')
    const cell = p.values?.[sk]?.[mk]
    if (cell != null && typeof cell === 'object' && !Array.isArray(cell)) {
      return true
    }
  }
  return false
}

function round2(n) {
  if (!Number.isFinite(n)) return 0
  return Number.parseFloat(n.toFixed(2))
}

/**
 * Agrega semanas em um número por mês para o gráfico.
 * Séries em dias (meta, realizado, médias de intervalo): média das semanas.
 * Demais formatos (%, R$): somatório das semanas.
 */
function monthValueFromWeeks(row, monthKey, weekKeys, fmt) {
  const block = row?.[monthKey]
  if (block == null || block === '') return 0
  if (typeof block === 'number' || typeof block === 'string') {
    const n = Number(block)
    return Number.isFinite(n) ? round2(n) : 0
  }
  let sum = 0
  for (const wk of weekKeys) {
    const n = Number(block?.[wk] ?? 0)
    sum += Number.isFinite(n) ? n : 0
  }
  if (fmt === 'days') {
    const len = weekKeys.length > 0 ? weekKeys.length : 1
    return round2(sum / len)
  }
  return round2(sum)
}

export function payloadToComboBarLineDualAxisChartjs(payload) {
  const p = payload || {}
  const categories = Array.isArray(p.categories) ? p.categories : []
  const seriesDefs = Array.isArray(p.series) ? p.series : []
  const values = p.values && typeof p.values === 'object' ? p.values : {}

  const defaultLabels = categories.map((c) => c?.label ?? '')
  const categoryKeys = categories.map((c) => c?.key ?? '')
  const xTickPattern = String(p.options?.xAxis?.monthTickPattern ?? '')
  const useAlternateShortMonths = xTickPattern === 'ptShortAlternate'
  const chartLabels = useAlternateShortMonths ? buildAlternateShortMonthLabels(categories) : defaultLabels

  const ordered = [...seriesDefs].sort((a, b) => Number(a?.order ?? 0) - Number(b?.order ?? 0))

  const allDaysMode =
    ordered.length > 0 && ordered.every((s) => String(s?.format ?? '') === 'days')

  const nestedWeeks = usesNestedWeekValues(p)
  const weekKeysAgg = nestedWeeks ? (Array.isArray(p.weekKeys) ? p.weekKeys.map(String) : []) : null

  const datasets = ordered.map((s) => {
    const key = String(s?.key ?? '')
    const row = values?.[key] || {}
    const fmt = String(s?.format ?? 'number')
    const isLine = String(s?.type ?? 'bar') === 'line'
    const axis = allDaysMode
      ? 'y-left'
      : String(s?.axis ?? 'left') === 'right'
        ? 'y-right'
        : 'y-left'
    const color = String(s?.color ?? '#2563eb')

    const data = categoryKeys.map((ck) => {
      const v = nestedWeeks
        ? monthValueFromWeeks(row, ck, weekKeysAgg, fmt)
        : Number(row?.[ck] ?? 0)
      if (isLine) {
        if (fmt === 'days') return Number.isFinite(v) ? v : null
        if (v === 0) return null
        return v
      }
      return v
    })

    const base = {
      label: String(s?.label ?? key),
      data,
      yAxisID: axis,
      _format: fmt,
    }

    if (isLine) {
      return {
        ...base,
        type: 'line',
        borderColor: color,
        backgroundColor: 'transparent',
        borderWidth: 2,
        pointBackgroundColor: color,
        pointBorderColor: color,
        pointRadius: 4,
        pointHoverRadius: 5,
        fill: false,
        spanGaps: false,
      }
    }

    return {
      ...base,
      backgroundColor: color,
      borderColor: color,
      borderWidth: 1,
    }
  })

  const left = p.options?.leftAxis || {}

  if (allDaysMode) {
    const leftDataMax = maxValueForYAxisId(datasets, 'y-left')
    const leftTickMax = tickMaxFromConfigAndData(
      Number(left.max),
      leftDataMax,
      Number(left.step),
    )

    const xTicks = useAlternateShortMonths
      ? {
          autoSkip: false,
          maxRotation: 0,
          minRotation: 0,
          fontColor: '#a1a1aa',
          fontSize: 11,
          callback(value) {
            if (value === ZWSP || value === '' || value == null) return ''
            return value
          },
        }
      : { maxRotation: 45, minRotation: 45 }

    return {
      data: { labels: chartLabels, datasets },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: {
            left: 56,
            right: 12,
            top: 28,
            bottom: useAlternateShortMonths ? 40 : 36,
          },
        },
        legend: { display: true, position: 'bottom', labels: { boxWidth: 12, padding: 12 } },
        plugins: {
          datalabels: { display: false },
        },
        tooltips: {
          mode: 'index',
          intersect: false,
          callbacks: {
            ...(useAlternateShortMonths
              ? {
                  title: (items) => {
                    const idx = items?.[0]?.index
                    if (idx == null) return ''
                    const cat = categories[idx]
                    return String(cat?.label ?? cat?.key ?? '')
                  },
                }
              : {}),
            label: (tooltipItem, data) => {
              const ds = data.datasets[tooltipItem.datasetIndex]
              const fmt = ds?._format || 'number'
              const label = ds?.label || ''
              const v = tooltipItem.yLabel
              return `${label}: ${formatTooltipValue(v, fmt)}`
            },
          },
        },
        scales: {
          xAxes: [
            {
              gridLines: { display: false },
              ticks: xTicks,
            },
          ],
          yAxes: [
            {
              id: 'y-left',
              position: 'left',
              type: 'linear',
              ticks: {
                beginAtZero: true,
                min: left.min != null ? Number(left.min) : 0,
                ...(leftTickMax != null ? { max: leftTickMax } : {}),
                ...(Number.isFinite(Number(left.step)) ? { stepSize: Number(left.step) } : {}),
                padding: 12,
                callback: (value) => {
                  const n = Number(value)
                  if (!Number.isFinite(n)) return String(value)
                  if (Number.isInteger(n)) return String(n)
                  return new Intl.NumberFormat('pt-BR', {
                    minimumFractionDigits: 0,
                    maximumFractionDigits: 2,
                  }).format(n)
                },
              },
              gridLines: { display: true },
            },
          ],
        },
      },
    }
  }

  const right = p.options?.rightAxis || {}
  const rightSeriesDef = ordered.find((s) => String(s?.axis ?? 'left') === 'right')
  const rightAxisFormat = String(rightSeriesDef?.format ?? 'currency')

  const rightDataMax = maxValueForYAxisId(datasets, 'y-right')
  const rightTickMax = tickMaxFromConfigAndData(
    Number(right.max),
    rightDataMax,
    Number(right.step),
  )

  return {
    data: { labels: chartLabels, datasets },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      layout: {
        padding: {
          left: 56,
          right: 14,
          top: 28,
          bottom: useAlternateShortMonths ? 40 : 36,
        },
      },
      legend: { display: true, position: 'bottom', labels: { boxWidth: 12, padding: 12 } },
      plugins: {
        datalabels: { display: false },
      },
      tooltips: {
        mode: 'index',
        intersect: false,
        callbacks: {
          ...(useAlternateShortMonths
            ? {
                title: (items) => {
                  const idx = items?.[0]?.index
                  if (idx == null) return ''
                  const cat = categories[idx]
                  return String(cat?.label ?? cat?.key ?? '')
                },
              }
            : {}),
          label: (tooltipItem, data) => {
            const ds = data.datasets[tooltipItem.datasetIndex]
            const fmt = ds?._format || 'number'
            const label = ds?.label || ''
            const v = tooltipItem.yLabel
            return `${label}: ${formatTooltipValue(v, fmt)}`
          },
        },
      },
      scales: {
        xAxes: [
          {
            gridLines: { display: false },
            ticks: useAlternateShortMonths
              ? {
                  autoSkip: false,
                  maxRotation: 0,
                  minRotation: 0,
                  fontColor: '#a1a1aa',
                  fontSize: 11,
                  callback(value) {
                    if (value === ZWSP || value === '' || value == null) return ''
                    return value
                  },
                }
              : { maxRotation: 45, minRotation: 45 },
          },
        ],
        yAxes: [
          {
            id: 'y-left',
            position: 'left',
            type: 'linear',
            ticks: {
              beginAtZero: true,
              min: 0,
              max: 100,
              stepSize: 20,
              padding: 12,
              callback: (value) => {
                const n = Number(value)
                if (!Number.isFinite(n)) return `${value}%`
                const rounded = Math.round(n * 100) / 100
                const s = Number.isInteger(rounded) ? String(rounded) : String(rounded).replace('.', ',')
                return `${s}%`
              },
            },
            gridLines: { display: true },
          },
          {
            id: 'y-right',
            position: 'right',
            type: 'linear',
            ticks: {
              beginAtZero: true,
              min: right.min != null ? Number(right.min) : 0,
              ...(rightTickMax != null ? { max: rightTickMax } : {}),
              ...(Number.isFinite(Number(right.step)) ? { stepSize: Number(right.step) } : {}),
              callback: (v) => {
                const n = Number(v)
                if (!Number.isFinite(n)) return String(v)
                if (rightAxisFormat === 'days') {
                  return `${Math.round(n)} d`
                }
                return new Intl.NumberFormat('pt-BR', {
                  style: 'currency',
                  currency: 'BRL',
                  maximumFractionDigits: 0,
                }).format(n)
              },
            },
            gridLines: { drawOnChartArea: false },
          },
        ],
      },
    },
  }
}
